<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import indicationService from "@/services/indication";
import medicationRequestService from "@/services/medicationRequest";

export default {
  name: "PatientPrescriptionRequest",
  components: {
    BaseSubNav,
    Spinner,
  },
  data() {
    return {
      selectedMedicationId: "",
      prescriptions: [],
      isLoading: false,
      isSubmitting: false,
      selectedIndication: null,
      medicationRequests: [],
      isLoadingRequests: false,
    };
  },
  computed: {
    uniqueMedications() {
      const medicationMap = new Map();

      const sortedPrescriptions = [...this.prescriptions].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      sortedPrescriptions.forEach((prescription) => {
        const medication =
          prescription.medication || prescription.publicMedication;
        if (!medication) return;

        const medicationKey = medication._id || medication.drugId;
        if (!medicationKey) return;

        if (!medicationMap.has(medicationKey)) {
          medicationMap.set(medicationKey, {
            id: medicationKey,
            name:
              medication.name ||
              `${medication.brandName || ""} - ${medication.drug || ""}`.trim(),
            presentation: medication.presentation || "",
            indication: prescription,
          });
        }
      });

      return Array.from(medicationMap.values());
    },
    medicationOptions() {
      return this.uniqueMedications.map((med) => ({
        value: med.id,
        label: med.presentation
          ? `${med.name} - ${med.presentation}`
          : med.name,
        indication: med.indication,
      }));
    },
  },
  async created() {
    await Promise.all([
      this.loadPrescriptions(),
      this.loadMedicationRequests(),
    ]);
  },
  methods: {
    async loadPrescriptions() {
      this.isLoading = true;
      try {
        const patientId = this.$store.state.user._id;
        const medicationsFilter = [{ type: "medications" }];
        const indications = await indicationService.get(
          patientId,
          medicationsFilter
        );
        this.prescriptions = indications || [];
      } catch (error) {
        console.error("Error al cargar las prescripciones:", error);
        this.$message.error("Error al cargar las prescripciones");
      } finally {
        this.isLoading = false;
      }
    },
    onMedicationChange(medicationId) {
      if (!medicationId) {
        this.selectedIndication = null;
        return;
      }

      const prescriptionsWithMedication = this.prescriptions
        .filter((prescription) => {
          const medication =
            prescription.medication || prescription.publicMedication;
          if (!medication) return false;
          const medicationKey = medication._id || medication.drugId;
          return medicationKey === medicationId;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      if (prescriptionsWithMedication.length > 0) {
        this.selectedIndication = prescriptionsWithMedication[0];
      } else {
        this.selectedIndication = null;
      }
    },
    async loadMedicationRequests() {
      this.isLoadingRequests = true;
      try {
        const patientId = this.$store.state.user._id;
        const res = await medicationRequestService.get({
          patientId: patientId,
        });
        this.medicationRequests = res.docs || res || [];
      } catch (error) {
        console.error("Error al cargar las solicitudes de recetas:", error);
        this.$message.error("Error al cargar las solicitudes de recetas");
      } finally {
        this.isLoadingRequests = false;
      }
    },
    getMedicationName(request) {
      if (request.medication?.name) {
        return request.medication.name;
      }
      if (request.publicMedication?.name) {
        return request.publicMedication.name;
      }
      if (request.publicMedication?.brandName) {
        return request.publicMedication.brandName;
      }
      return "Sin medicación";
    },
    getDiagnosisName(request) {
      if (typeof request.diagnosis === "string") {
        return request.diagnosis;
      }
      if (request.diagnosis?.name) {
        return request.diagnosis.name;
      }
      if (typeof request.publicDiagnosis === "string") {
        return request.publicDiagnosis;
      }
      if (request.publicDiagnosis?.name) {
        return request.publicDiagnosis.name;
      }
      return "Sin diagnóstico";
    },
    getStatusText(status) {
      const statusMap = {
        pending: "Pendiente",
        approved: "Aprobada",
        rejected: "Rechazada",
        cancelled: "Cancelada",
      };
      return statusMap[status] || status;
    },
    async cancelRequest(request) {
      try {
        await this.$confirm(
          "¿Desea cancelar esta solicitud de medicamento?",
          "Cancelar solicitud",
          {
            confirmButtonText: "Cancelar",
            cancelButtonText: "No",
            type: "warning",
          }
        );

        await medicationRequestService.update(request._id, {
          status: "cancelled",
        });

        const index = this.medicationRequests.findIndex(
          (r) => r._id === request._id
        );
        if (index !== -1) {
          this.medicationRequests[index].status = "cancelled";
        }

        this.$message({
          type: "success",
          message: "Solicitud cancelada exitosamente",
        });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error canceling medication request:", error);
          this.$message({
            type: "error",
            message: "Error al cancelar la solicitud",
          });
        }
      }
    },
    async handleRequestPrescription() {
      if (!this.selectedMedicationId) {
        this.$message.warning("Por favor seleccione un medicamento");
        return;
      }

      if (!this.selectedIndication) {
        this.$message.warning(
          "No se encontró la información de la prescripción"
        );
        return;
      }

      this.isSubmitting = true;
      try {
        const requestData = {
          patientId: this.$store.state.user._id,
          ...this.selectedIndication,
        };

        await medicationRequestService.create(requestData);

        this.$message.success("Solicitud de receta enviada correctamente");
        this.selectedMedicationId = "";
        this.selectedIndication = null;
        await this.loadMedicationRequests();
      } catch (error) {
        console.error("Error al enviar la solicitud:", error);
        const errorMessage =
          error.response?.data?.error ||
          "Error al enviar la solicitud de receta";
        this.$message.error(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav
    template(#title)
      | Pedir receta
  .request-prescription-container
    el-card.request-prescription-card
      el-form(:model="$data")
        el-form-item.form-item-full(label="Seleccione el medicamento que desea pedir")
          el-select(
            v-model="selectedMedicationId"
            filterable
            placeholder="Seleccione el medicamento que desea pedir"
            :loading="isLoading"
            @input="onMedicationChange"
            clearable
          )
            el-option(
              v-for="option in medicationOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            )
        el-form-item.form-buttons-container
          el-button.form-button-primary(
            type="primary"
            @click="handleRequestPrescription"
            :loading="isSubmitting"
            :disabled="isSubmitting"
          ) Pedir receta

  .box.box--with-subnav
    .box__content--stretch
      spinner(v-if="isLoadingRequests")
      h2.no-results(v-if="!isLoadingRequests && medicationRequests.length === 0")
        | No tienes solicitudes de medicamentos
      table(v-if="!isLoadingRequests && medicationRequests.length")
        thead
          tr
            th Medicación
            th Diagnóstico
            th Estado
            th Fecha de solicitud
            th Acciones
        tbody
          tr(v-for="request in medicationRequests" :key="request._id")
            td {{ getMedicationName(request) }}
            td {{ getDiagnosisName(request) }}
            td
              span.status-badge(:class="'status-' + request.status")
                | {{ getStatusText(request.status) }}
            td {{ request.createdAt | formatDateAndTime }}
            td.actions
              .actions-container
                button.button.button--action.danger(
                  v-if="request.status === 'pending'"
                  @click.stop="cancelRequest(request)"
                  v-tooltip="'Cancelar'"
                )
                  micon(name="clear")
</template>

<style lang="scss" scoped>
.request-prescription-container {
  width: 100%;
}

.request-prescription-card {
  max-width: 800px;
  margin: 0 auto;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.form-item-full {
  width: 100%;
  margin-bottom: 1.5rem;

  ::v-deep .el-form-item__label {
    font-weight: 500;
    color: #040055;
    margin-bottom: 0.5rem;
  }
}

.form-buttons-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  margin-bottom: 0;
}

.form-button-primary {
  background-color: #040055;
  border-color: #040055;
  padding: 12px 24px;
  border-radius: 15px;

  &:hover {
    background-color: #2a2a8a;
    border-color: #2a2a8a;
  }
}

.box--with-subnav {
  margin-top: 2rem;
}

.no-results {
  text-align: center;
  padding: 50px;
  font-size: 1.5em;
  width: 100%;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;

  &.status-pending {
    background-color: #fdf6ec;
    color: #e6a23c;
  }

  &.status-approved {
    background-color: #f0f9ff;
    color: #67c23a;
  }

  &.status-rejected {
    background-color: #fef0f0;
    color: #f56c6c;
  }

  &.status-cancelled {
    background-color: #f4f4f5;
    color: #909399;
  }
}

tbody td:hover {
  cursor: pointer;
}

tbody td.actions {
  cursor: default;
}

.actions-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.button--action {
  border: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;

  &.danger {
    background-color: #f56c6c;

    &:hover {
      background-color: #f45656;
    }

    .micon {
      fill: white;
    }
  }
}
</style>
